import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { fetchBuyerOrderDetail } from '../../lib/buyerApi';
import { DEFAULT_CURRENCY, formatMoney } from '../../components/marketplace/cartUtils';

type Tab = 'summary' | 'items' | 'suppliers';
const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'items', label: 'Items' },
  { key: 'suppliers', label: 'Suppliers' },
];

const BuyerOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [supplierOrders, setSupplierOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('summary');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetchBuyerOrderDetail(parseInt(id));
      setOrder(res.order);
      setItems(res.items || []);
      setSupplierOrders(res.supplierOrders || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <BuyerPortalLayout title="Order Detail">
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </BuyerPortalLayout>
    );
  }

  if (!order) {
    return (
      <BuyerPortalLayout title="Order Not Found">
        <div className="text-center py-16">
          <p className="text-gray-400">Order not found</p>
          <Link to="/account/orders" className="mt-3 text-sm text-emerald-600 font-bold hover:underline">← Back to Orders</Link>
        </div>
      </BuyerPortalLayout>
    );
  }

  return (
    <BuyerPortalLayout title={order.order_number}>
      {/* Back */}
      <Link to="/account/orders" className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-block">← Back to Orders</Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-extrabold text-gray-900">{order.order_number}</h2>
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
              order.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
              order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
              'bg-amber-100 text-amber-700'
            }`}>
              {(order.status || '').replace(/_/g, ' ')}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Created {new Date(order.created_at).toLocaleDateString()}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-extrabold text-gray-900">{formatMoney(Number(order.total_amount) || 0, order || DEFAULT_CURRENCY)}</p>
          <p className="text-xs text-gray-400">{items.length} items · {supplierOrders.length} supplier{supplierOrders.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex gap-0">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.key ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'summary' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-100 rounded-2xl p-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Order Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Buyer</span><span>{order.buyer_name || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Organisation</span><span>{order.buyer_org_name || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Delivery Address</span><span className="text-right max-w-[200px] truncate">{order.delivery_address || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Delivery Date</span><span>{order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Payment Terms</span><span>{order.payment_terms || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Notes</span><span className="text-right max-w-[200px] truncate">{order.notes || '—'}</span></div>
            </div>
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl p-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Totals</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatMoney(Number(order.subtotal) || 0, order || DEFAULT_CURRENCY)}</span></div>
              <div className="flex justify-between border-t border-gray-100 pt-2"><span className="font-bold text-gray-900">Total</span><span className="font-bold text-lg">{formatMoney(Number(order.total_amount) || 0, order || DEFAULT_CURRENCY)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Created</span><span>{new Date(order.created_at).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Updated</span><span>{new Date(order.updated_at).toLocaleString()}</span></div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'items' && (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Product</th>
                <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Grade</th>
                <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Qty</th>
                <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Unit Price</th>
                <th className="px-5 py-3 text-right font-bold text-gray-500 text-xs uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map((item: any) => (
                <tr key={item.id}>
                  <td className="px-5 py-3 font-medium text-gray-900">{item.title}</td>
                  <td className="px-5 py-3 text-gray-500">{item.grade || '—'}</td>
                  <td className="px-5 py-3 text-gray-600">{item.commercial_quantity} {item.commercial_unit}</td>
                  <td className="px-5 py-3 text-gray-600">{formatMoney(Number(item.unit_price) || 0, item || DEFAULT_CURRENCY)}</td>
                  <td className="px-5 py-3 text-right font-bold">{formatMoney(Number(item.total_price) || 0, item || DEFAULT_CURRENCY)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-gray-200 bg-gray-50">
              <tr>
                <td colSpan={4} className="px-5 py-3 font-bold text-gray-900">Total</td>
                <td className="px-5 py-3 text-right font-bold text-lg">{formatMoney(Number(order.total_amount) || 0, order || DEFAULT_CURRENCY)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {activeTab === 'suppliers' && (
        <div className="space-y-3">
          {supplierOrders.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center">
              <p className="text-gray-400">No supplier orders</p>
            </div>
          ) : (
            supplierOrders.map((so: any) => (
              <div key={so.id} className="bg-white border border-gray-100 rounded-2xl p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-gray-900">{so.order_number}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{so.supplier_name || `Supplier #${so.supplier_tenant_id}`}</p>
                    <span className={`mt-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      so.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      so.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {(so.status || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-lg font-extrabold text-gray-900">{formatMoney(Number(so.total_amount) || 0, so || DEFAULT_CURRENCY)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </BuyerPortalLayout>
  );
};

export default BuyerOrderDetailPage;
